import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginationConfig, TableColumn, TableComponent, withCellTemplates, ModalComponent } from '../../../shared/components/primeng';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  AclApdApiService,
  parsePolicyExpiryIsoToMillis,
  utcToIst,
} from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { ActionDropdownTriggerDirective } from '../../../shared/directives/action-dropdown-trigger.directive';
import { ActionDropdownOverlayController } from '../../../shared/utils/action-dropdown-overlay.sync';
import { ToasterService } from '../../../services/toaster.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';
import { readAccessRequestPurpose } from '../../../shared/utils/access-request-purpose';
import { AddPolicyHomeComponent } from '../../add-policy-home/add-policy-home.component';

interface PolicyAccessConstraintDetail {
  accessType: string;
  expiry: string;
}

interface PolicyPersonDetails {
  firstName: string;
  lastName: string;
  email: string;
  organization: string;
}

interface PolicyDetailsView {
  status: string;
  policyType: string;
  createdAt: string;
  updatedAt: string;
  expiryAt: string;
  owner: PolicyPersonDetails;
  user: PolicyPersonDetails;
  asset: {
    assetName: string;
    assetType: string;
    shortDescription: string;
    organization: string;
  };
  accessConstraints: PolicyAccessConstraintDetail[];
  allowedRoles: string[];
  allowedOrgs: string[];
  providerComment: string;
  feedbackToConsumer: string;
}

interface CreatedPolicyRow {
  username: string;
  assetName: string;
  accessTypes: string[];
  createdAtText: string;
  expiryAtText: string;
  policyStatus: string;
  approvedByName: string;
  approvedByRole: string;
  createdByName: string;
  createdByRole: string;
  originalData?: Record<string, unknown>;
}

type PolicyStatusFilter = 'ACTIVE' | 'DELETED';

@Component({
  selector: 'app-created-policies',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ActionDropdownTriggerDirective,
    ModalComponent,
    ConfirmationDialogComponent,
    AddPolicyHomeComponent,
  ],
  templateUrl: './created-policies.component.html',
  styleUrl: './created-policies.component.scss',
})
export class CreatedPoliciesComponent implements OnInit, OnChanges, AfterViewInit {
  /** Portal determines which policy list API is used. */
  @Input() policyPortal: 'org-admin' | 'provider' | 'consumer' | 'platform' = 'org-admin';
  /** Whether to load created policies or owned policies. */
  @Input() policyListType: 'created' | 'owned' = 'created';
  /** Optional policyType query filter(s), e.g. ACCESS_REQUEST_APPROVED or INDIVIDUAL+GROUP. */
  @Input() policyTypes?: string | string[];
  /** When false, hides the toolbar Add New Policy button (e.g. hosted by Granted Access tabs). */
  @Input() showToolbarAddPolicyButton = true;
  /** Route prefix for the request conversation page; shown for approved, revoked, and expired requests. */
  @Input() conversationRoute: string | null = null;

  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<unknown>;
  @ViewChild('accessTypeCell', { static: false }) accessTypeCellTemplate!: TemplateRef<unknown>;
  @ViewChild('policyStatusCell', { static: false }) policyStatusCellTemplate!: TemplateRef<unknown>;
  @ViewChild('approvedByCell', { static: false }) approvedByCellTemplate!: TemplateRef<unknown>;
  @ViewChild('createdByCell', { static: false }) createdByCellTemplate!: TemplateRef<unknown>;

  private readonly baseColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: false },
    { key: 'username', label: 'Username', sortable: false },
    { key: 'accessType', label: 'Access Constraints', sortable: false },
    { key: 'createdAtText', label: 'Created At', sortable: false },
    { key: 'createdBy', label: 'Created By', sortable: false, width: '200px' },
    { key: 'expiryAtText', label: 'Expires At', sortable: false },
    { key: 'policyStatus', label: 'Policy Status', sortable: false },
    { key: 'approvedBy', label: 'Approved By', sortable: false, width: '200px' },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  columns: TableColumn[] = [...this.baseColumns];

  rows: CreatedPolicyRow[] = [];
  openDropdownId: string | null = null;
  dropdownPosition: { top: number; left: number | null; right: number | null } = {
    top: 0,
    left: null,
    right: null,
  };
  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();
  detailsModalVisible = false;
  selectedPolicyDetails: PolicyDetailsView | null = null;
  showDeleteConfirmModal = false;
  rowToDelete: CreatedPolicyRow | null = null;
  isDeleting = false;
  addPolicyModalVisible = false;
  loading = false;
  emptyMessage = 'No active policies found';
  selectedPolicyStatus: PolicyStatusFilter = 'ACTIVE';
  readonly policyStatusTabs: Array<{ label: string; value: PolicyStatusFilter }> = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Revoked', value: 'DELETED' },
  ];
  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };

  /** Dedupes identical concurrent/back-to-back policy list fetches (e.g. paginator echo). */
  private inFlightLoadKey: string | null = null;
  private lastSuccessfulLoadKey: string | null = null;
  private loadRequestId = 0;

  constructor(
    private readonly aclApdApi: AclApdApiService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toasterService: ToasterService,
    private readonly keycloak: KeycloakService,
    private readonly workspaceContext: WorkspaceContextService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const policyStatus = this.route.snapshot.queryParamMap.get('policyStatus');
    if (policyStatus === 'ACTIVE' || policyStatus === 'DELETED') {
      this.selectedPolicyStatus = policyStatus;
    }
    this.syncPolicyColumns();
    this.syncEmptyMessage();
    void this.loadCreatedPolicies();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const policyTypesChange = changes['policyTypes'];
    const portalChange = changes['policyPortal'];
    const listTypeChange = changes['policyListType'];
    if (
      (policyTypesChange && !policyTypesChange.firstChange) ||
      (portalChange && !portalChange.firstChange) ||
      (listTypeChange && !listTypeChange.firstChange)
    ) {
      this.syncPolicyColumns();
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.lastSuccessfulLoadKey = null;
      void this.loadCreatedPolicies({ force: true });
    }
  }

  get showAddPolicyButton(): boolean {
    return (
      this.showToolbarAddPolicyButton &&
      this.policyListType === 'created' &&
      (this.policyPortal === 'org-admin' || this.policyPortal === 'provider')
    );
  }

  openAddPolicyModal(): void {
    this.closeActionDropdown();
    this.addPolicyModalVisible = true;
  }

  closeAddPolicyModal(): void {
    this.addPolicyModalVisible = false;
  }

  onAddPolicySaved(): void {
    this.addPolicyModalVisible = false;
    this.pagination = { ...this.pagination, currentPage: 1 };
    void this.loadCreatedPolicies({ force: true });
  }

  setPolicyStatusTab(status: PolicyStatusFilter): void {
    if (this.selectedPolicyStatus === status) {
      return;
    }
    this.closeActionDropdown();
    this.selectedPolicyStatus = status;
    this.syncPolicyColumns();
    this.syncEmptyMessage();
    this.pagination = { ...this.pagination, currentPage: 1 };
    void this.loadCreatedPolicies({ force: true });
  }

  private syncEmptyMessage(): void {
    if (
      this.policyPortal === 'consumer' ||
      this.policyListType === 'owned' ||
      this.hasPolicyTypeFilter()
    ) {
      this.emptyMessage =
        this.selectedPolicyStatus === 'DELETED'
          ? 'No revoked owner policies found'
          : 'No active owner policies found';
      return;
    }
    this.emptyMessage =
      this.selectedPolicyStatus === 'DELETED'
        ? 'No revoked policies found'
        : 'No active policies found';
  }

  ngAfterViewInit(): void {
    this.syncPolicyColumns();
    this.cdr.detectChanges();
  }

  /** Update columns for active/deleted tab and re-bind cell templates. */
  private syncPolicyColumns(): void {
    const dateLabel = this.selectedPolicyStatus === 'DELETED' ? 'Revoked At' : 'Created At';
    const showExpiresAt = this.selectedPolicyStatus !== 'DELETED';
    const showApprovedBy = this.shouldShowApprovedByColumn();
    const showCreatedBy = this.shouldShowCreatedByColumn();
    this.columns = this.baseColumns
      .filter((col) => {
        if (!showExpiresAt && (col.key === 'expiryAtText' || col.key === 'policyStatus')) {
          return false;
        }
        if (!showApprovedBy && col.key === 'approvedBy') {
          return false;
        }
        if (!showCreatedBy && col.key === 'createdBy') {
          return false;
        }
        return true;
      })
      .map((col) =>
        col.key === 'createdAtText' ? { ...col, label: dateLabel } : { ...col },
      );
    const cellTemplates: Array<{ key: string; cellTemplate: TemplateRef<unknown> }> = [];
    if (this.accessTypeCellTemplate) {
      cellTemplates.push({ key: 'accessType', cellTemplate: this.accessTypeCellTemplate });
    }
    if (this.policyStatusCellTemplate) {
      cellTemplates.push({ key: 'policyStatus', cellTemplate: this.policyStatusCellTemplate });
    }
    if (this.approvedByCellTemplate) {
      cellTemplates.push({ key: 'approvedBy', cellTemplate: this.approvedByCellTemplate });
    }
    if (this.createdByCellTemplate) {
      cellTemplates.push({ key: 'createdBy', cellTemplate: this.createdByCellTemplate });
    }
    if (this.actionCellTemplate) {
      cellTemplates.push({ key: 'action', cellTemplate: this.actionCellTemplate });
    }
    if (cellTemplates.length) {
      this.columns = withCellTemplates(this.columns, cellTemplates);
    }
  }

  /** Access Policy page (provider / org-admin / platform) shows who created the policy. */
  private shouldShowCreatedByColumn(): boolean {
    return !this.shouldShowApprovedByColumn();
  }

  /** Approved Request / Issued policies (Granted Access) tables show who approved or issued the policy. */
  private shouldShowApprovedByColumn(): boolean {
    return this.policyPortal === 'consumer' || this.hasPolicyTypeFilter();
  }

  onPageChange(page: number): void {
    if (page === this.pagination.currentPage) {
      return;
    }
    this.pagination = { ...this.pagination, currentPage: page };
    void this.loadCreatedPolicies();
  }

  onPageSizeChange(pageSize: number): void {
    if (pageSize === this.pagination.pageSize && this.pagination.currentPage === 1) {
      return;
    }
    this.pagination = { ...this.pagination, pageSize, currentPage: 1 };
    void this.loadCreatedPolicies({ force: true });
  }

  private hasPolicyTypeFilter(): boolean {
    const types = this.policyTypes;
    if (Array.isArray(types)) {
      return types.some((type) => !!String(type ?? '').trim());
    }
    return !!String(types ?? '').trim();
  }

  private fetchPoliciesForPortal(page: number, size: number) {
    const status = this.selectedPolicyStatus ?? 'ACTIVE';
    const policyType = this.policyTypes;
    const hasPolicyTypeFilter = this.hasPolicyTypeFilter();
    // When filtering by policyType (Granted Access), do not also filter by ownerId.
    const ownerId = hasPolicyTypeFilter ? undefined : this.resolvePolicyOwnerId();

    // Granted Access / owned policies always use GET …/policy/consumer
    // (e.g. policyType=ACCESS_REQUEST_APPROVED or policyType=INDIVIDUAL&policyType=GROUP).
    if (
      hasPolicyTypeFilter ||
      this.policyListType === 'owned' ||
      this.policyPortal === 'consumer'
    ) {
      return this.aclApdApi.getConsumerPolicies(page, size, status, policyType);
    }
    if (this.policyPortal === 'platform') {
      return this.aclApdApi.getPlatformPolicies(page, size, status, policyType);
    }
    if (this.policyPortal === 'provider') {
      return this.aclApdApi.getProviderPolicies(page, size, status, ownerId, policyType);
    }

    return this.aclApdApi.getOrganisationPolicies(page, size, status, ownerId, policyType);
  }

  /**
   * Delegate sessions send the same owner user id as the `did` header.
   * Every other session keeps the logged-in user's Keycloak id.
   */
  private resolvePolicyOwnerId(): string {
    if (this.workspaceContext.isDelegateSession()) {
      return (this.workspaceContext.getActiveDelegatorId() || '').trim();
    }
    return this.getOwnerIdFromKeycloak();
  }

  private getOwnerIdFromKeycloak(): string {
    try {
      const instance = this.keycloak.getKeycloakInstance();
      const parsed = (instance as { tokenParsed?: { sub?: string } })?.tokenParsed;
      const sub = parsed?.sub ?? (instance as { subject?: string })?.subject;
      return sub && typeof sub === 'string' ? sub.trim() : '';
    } catch {
      return '';
    }
  }

  private buildPolicyLoadKey(): string {
    const types = this.policyTypes;
    const typeKey = Array.isArray(types) ? types.join(',') : String(types ?? '');
    return [
      this.policyPortal,
      this.policyListType,
      this.selectedPolicyStatus ?? 'ACTIVE',
      this.pagination.currentPage,
      this.pagination.pageSize,
      typeKey,
      this.resolvePolicyOwnerId(),
    ].join('|');
  }

  private async loadCreatedPolicies(options?: { force?: boolean }): Promise<void> {
    const loadKey = this.buildPolicyLoadKey();
    if (!options?.force) {
      if (this.inFlightLoadKey === loadKey || this.lastSuccessfulLoadKey === loadKey) {
        return;
      }
    }

    const requestId = ++this.loadRequestId;
    this.inFlightLoadKey = loadKey;
    this.loading = true;
    try {
      const page = this.pagination.currentPage;
      const size = this.pagination.pageSize;

      if (
        this.policyListType === 'created' &&
        (this.policyPortal === 'org-admin' || this.policyPortal === 'provider') &&
        !this.resolvePolicyOwnerId()
      ) {
        this.rows = [];
        this.pagination = { ...this.pagination, totalItems: 0 };
        this.lastSuccessfulLoadKey = loadKey;
        return;
      }

      const response = await this.fetchPoliciesForPortal(page, size);
      if (requestId !== this.loadRequestId) {
        return;
      }
      const items = this.extractList(response);
      const totalItems = this.extractTotal(response, items.length);
      this.rows = items.map((item) => this.mapPolicyRow(item));
      this.pagination = { ...this.pagination, totalItems };
      this.lastSuccessfulLoadKey = loadKey;
    } catch (error) {
      if (requestId !== this.loadRequestId) {
        return;
      }
      console.error('Error loading created policies:', error);
      this.rows = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
      this.lastSuccessfulLoadKey = null;
    } finally {
      if (requestId === this.loadRequestId) {
        this.loading = false;
        if (this.inFlightLoadKey === loadKey) {
          this.inFlightLoadKey = null;
        }
      }
    }
  }

  private mapPolicyRow(item: Record<string, unknown>): CreatedPolicyRow {
    const asset = this.readObject(item, 'asset');
    const constraints = this.readObject(item, 'constraints');
    const user = this.readObject(item, 'user');
    const owner = this.readObject(item, 'owner');

    const expiryAt =
      this.readFirstString(item, ['expiryAt', 'expiry_at', 'expiryTime', 'expiry_time', 'expiresAt', 'expires_at']) ||
      this.readExpiryFromConstraints(constraints);
    const createdAt = this.readFirstString(item, ['createdAt', 'created_at', 'itemCreatedAt', 'item_created_at']);
    const updatedAt = this.readFirstString(item, ['updatedAt', 'updated_at']);
    const dateForColumn =
      this.selectedPolicyStatus === 'DELETED' ? updatedAt : createdAt;
    const firstName = this.readFirstString(user ?? item, ['consumerFirstName', 'consumer_first_name']);
    const lastName = this.readFirstString(user ?? item, ['consumerLastName', 'consumer_last_name']);
    const email = this.readFirstString(user ?? item, ['consumerEmail', 'consumer_email']);
    const usernameFromUser =
      this.formatUserDisplayName(firstName, lastName, email) || email;
    const ownerOrganization = this.readFirstString(owner ?? {}, [
      'ownerOrganization',
      'owner_organization',
      'organization',
      'organisation',
    ]);
    const username =
      usernameFromUser?.trim() && usernameFromUser !== '-'
        ? usernameFromUser
        : ownerOrganization?.trim() || '-';
    const approvedBy = this.readApprovedBy(item, owner);
    const createdBy = this.readCreatedBy(item, owner);

    return {
      username,
      assetName: this.readFirstString(asset ?? item, ['itemName', 'assetName', 'asset_name', 'name']) || '-',
      accessTypes: this.readAccessTypes(constraints),
      createdAtText: this.formatDateText(dateForColumn),
      expiryAtText: this.formatExpiryDateText(expiryAt),
      policyStatus: this.resolvePolicyStatus(item, expiryAt),
      approvedByName: approvedBy.name,
      approvedByRole: approvedBy.role,
      createdByName: createdBy.name,
      createdByRole: createdBy.role,
      originalData: item,
    };
  }

  hasApprovedByValue(row: CreatedPolicyRow): boolean {
    return !!row.approvedByName && row.approvedByName !== '-';
  }

  hasCreatedByValue(row: CreatedPolicyRow): boolean {
    return !!row.createdByName && row.createdByName !== '-';
  }

  private resolvePolicyStatus(item: Record<string, unknown>, expiryAt: string): string {
    const rawStatus = this.readFirstString(item, ['status', 'policyStatus', 'policy_status']);
    if (rawStatus) {
      const normalized = rawStatus.toUpperCase();
      if (normalized === 'DELETED') {
        return 'Revoked';
      }
      if (normalized === 'ACTIVE') {
        return this.resolveExpiryStatus(expiryAt);
      }
      return this.toTitleCase(rawStatus);
    }
    return this.resolveExpiryStatus(expiryAt);
  }

  private resolveExpiryStatus(expiryAt: string): string {
    const raw = String(expiryAt ?? '').trim();
    if (!raw) {
      return '-';
    }
    const millis = parsePolicyExpiryIsoToMillis(raw);
    if (!millis) {
      return '-';
    }
    return millis < Date.now() ? 'Expired' : 'Active';
  }

  formatAccessTypeLabel(value: string): string {
    return this.toTitleCase(value);
  }

  getAccessTypeChipClass(value: string): string {
    const key = String(value ?? '').toLowerCase().trim();
    if (key === 'api') {
      return 'created-policies-chip--api';
    }
    if (key === 'file') {
      return 'created-policies-chip--file';
    }
    if (key === 'sub' || key === 'subscription') {
      return 'created-policies-chip--sub';
    }
    if (key === 'attr' || key === 'attribute') {
      return 'created-policies-chip--attr';
    }
    return 'created-policies-chip--default';
  }

  canConverse(row: CreatedPolicyRow): boolean {
    if (this.workspaceContext.isDelegateSession()) return false;
    const requestId = this.getConversationRequestId(row);
    const status = this.conversationStatus(row);
    return !!this.conversationRoute && this.isApprovedRequestPolicy() && !!requestId && !!status;
  }

  openConversation(row: CreatedPolicyRow): void {
    const requestId = this.getConversationRequestId(row);
    if (!this.conversationRoute || !requestId) {
      return;
    }
    this.closeActionDropdown();
    // Approved policies keep the purpose on additionalInfo, where the pending request stored it.
    const purpose = readAccessRequestPurpose(row.originalData ?? {});
    void this.router.navigate([this.conversationRoute, requestId, 'conversation'], {
      queryParams: {
        tab: 'createdPolicies',
        grantedSubTab: 'approved',
        policyStatus: this.selectedPolicyStatus,
      },
      state: { assetName: row.assetName, purpose, status: this.conversationStatus(row) },
    });
  }

  private conversationStatus(row: CreatedPolicyRow): string {
    const status = row.policyStatus.toLowerCase();
    if (status === 'expired' || status === 'revoked') {
      return status;
    }
    return status === 'active' ? 'approved' : '';
  }

  private isApprovedRequestPolicy(): boolean {
    const types = this.policyTypes;
    const values = Array.isArray(types) ? types : types ? [types] : [];
    return values.some((type) => type === 'ACCESS_REQUEST_APPROVED');
  }

  private getConversationRequestId(row: CreatedPolicyRow): string {
    const item = row.originalData ?? {};
    const requestId = this.readFirstString(item, [
      'accessRequestId',
      'access_request_id',
      'requestId',
      'request_id',
    ]);
    if (requestId) {
      return requestId;
    }
    const nested = item['accessRequest'] ?? item['access_request'];
    if (nested && typeof nested === 'object') {
      return this.readFirstString(nested as Record<string, unknown>, ['id', 'requestId', 'request_id']);
    }
    return '';
  }

  getRowId(row: CreatedPolicyRow): string {
    const data = row.originalData;
    const id = data?.['policyId'] ?? data?.['id'] ?? data?.['_id'];
    if (id != null && String(id).trim()) {
      return String(id);
    }
    return `${row.assetName}-${row.createdAtText}`;
  }

  toggleActionDropdown(event: Event, row: CreatedPolicyRow): void {
    event.stopPropagation();
    const rowId = this.getRowId(row);
    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }
    const button =
      (event.currentTarget as HTMLElement | null) ??
      ((event.target as HTMLElement).closest('.action-btn') as HTMLElement | null);
    if (!button) {
      return;
    }
    this.openDropdownId = rowId;
    this.actionDropdownOverlay.open({
      anchor: button,
      itemCount: (this.showDeletePolicyAction(row) ? 2 : 1) + (this.canConverse(row) ? 1 : 0),
      minWidth: 0,
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.detectChanges();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownId = null;
  }

  isDropdownOpen(row: CreatedPolicyRow): boolean {
    return this.openDropdownId === this.getRowId(row);
  }

  get openDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) {
      return null;
    }
    const idx = this.rows.findIndex((r) => this.getRowId(r) === this.openDropdownId);
    return idx >= 0 ? idx : null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  private formatDateText(value: string): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '-';
    }
    return utcToIst(raw) || '-';
  }

  private formatExpiryDateText(value: string): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '-';
    }
    return utcToIst(raw) || '-';
  }

  /** Common expiry field names on policy and constraint access rows. */
  private readonly expiryFieldKeys = [
    'expiry',
    'expiryTime',
    'expiry_time',
    'expiryAt',
    'expiry_at',
    'expiresAt',
    'expires_at',
  ];

  private toTitleCase(value: string): string {
    return value
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private readFirstString(item: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = item[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  private readObject(item: Record<string, unknown>, key: string): Record<string, unknown> | null {
    const value = item[key];
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    return value as Record<string, unknown>;
  }

  private readExpiryFromConstraints(
    constraints: Record<string, unknown> | null,
  ): string {
    if (!constraints) {
      return '';
    }
    const access = constraints['access'];
    if (!Array.isArray(access) || access.length === 0) {
      return '';
    }
    const first = access[0];
    if (!first || typeof first !== 'object' || Array.isArray(first)) {
      return '';
    }
    return this.readFirstString(first as Record<string, unknown>, this.expiryFieldKeys);
  }

  private readAccessTypes(
    constraints: Record<string, unknown> | null,
  ): string[] {
    if (!constraints) {
      return [];
    }
    const access = constraints['access'];
    if (!Array.isArray(access) || access.length === 0) {
      return [];
    }
    const types = new Set<string>();
    for (const entry of access) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        continue;
      }
      const accessType = this.readFirstString(entry as Record<string, unknown>, ['accessType', 'type']);
      if (accessType) {
        types.add(accessType);
      }
    }
    return Array.from(types);
  }

  onViewDetails(row: CreatedPolicyRow): void {
    this.closeActionDropdown();
    this.selectedPolicyDetails = this.buildPolicyDetails(row.originalData ?? {});
    // Open on the next tick so dropdown teardown does not swallow the dialog paint
    // (otherwise the modal only appears after a subsequent outside click).
    setTimeout(() => {
      this.detailsModalVisible = true;
      this.cdr.detectChanges();
    });
  }

  showDeletePolicyAction(row: CreatedPolicyRow): boolean {
    // Platform admin Created Policies is view-only for active records — no delete.
    if (this.policyPortal === 'platform') {
      return false;
    }
    // Granted Access (owned) and consumer policy lists are view-only — no delete.
    if (this.policyPortal === 'consumer' || this.policyListType === 'owned') {
      return false;
    }
    if (this.selectedPolicyStatus !== 'ACTIVE') {
      return false;
    }
    return !this.isPolicyExpired(row);
  }

  private isPolicyExpired(row: CreatedPolicyRow): boolean {
    return String(row.policyStatus ?? '').toLowerCase() === 'expired';
  }

  onDeletePolicy(row: CreatedPolicyRow): void {
    this.closeActionDropdown();
    this.rowToDelete = row;
    // Open on the next tick so dropdown teardown does not swallow the dialog paint
    // (otherwise the modal only appears after a subsequent outside click).
    setTimeout(() => {
      this.showDeleteConfirmModal = true;
      this.cdr.detectChanges();
    });
  }

  onConfirmDeletePolicy(): void {
    if (!this.rowToDelete) {
      return;
    }
    void this.performDeletePolicy(this.rowToDelete);
  }

  onCancelDeletePolicy(): void {
    this.showDeleteConfirmModal = false;
    this.rowToDelete = null;
  }

  getDeletePolicyConfirmMessage(): string {
    if (!this.rowToDelete) {
      return '';
    }
    const assetName = this.rowToDelete.assetName || 'this asset';
    return `Are you sure you want to revoke the policy for "<strong>${assetName}</strong>"? This action cannot be undone.`;
  }

  private getPolicyId(row: CreatedPolicyRow): string | null {
    const data = row.originalData;
    const id = data?.['policyId'] ?? data?.['id'] ?? data?.['_id'];
    if (id == null || !String(id).trim()) {
      return null;
    }
    return String(id).trim();
  }

  private async performDeletePolicy(row: CreatedPolicyRow): Promise<void> {
    const policyId = this.getPolicyId(row);
    if (!policyId) {
      this.toasterService.error('Unable to revoke policy: missing policy ID');
      this.onCancelDeletePolicy();
      return;
    }

    this.isDeleting = true;

    try {
      await this.aclApdApi.deletePolicy(policyId);
      this.toasterService.success('Policy Revoked Successfully');
      this.onCancelDeletePolicy();

      if (this.rows.length <= 1 && this.pagination.currentPage > 1) {
        this.pagination = { ...this.pagination, currentPage: this.pagination.currentPage - 1 };
      }
      this.selectedPolicyStatus = 'ACTIVE';
      this.syncPolicyColumns();
      this.syncEmptyMessage();
      await this.loadCreatedPolicies({ force: true });
    } catch (error) {
      console.error('Error deleting policy:', error);
      this.toasterService.error(
        this.readApiErrorDetail(error, 'Failed to delete policy. Please try again.'),
      );
    } finally {
      this.isDeleting = false;
    }
  }

  private readApiErrorDetail(error: unknown, fallback: string): string {
    const err = error as { error?: { detail?: string }; message?: string };
    const detail = err?.error?.detail ?? err?.message;
    if (detail != null && String(detail).trim()) {
      return String(detail).trim();
    }
    return fallback;
  }

  closeDetailsModal(): void {
    this.detailsModalVisible = false;
    this.selectedPolicyDetails = null;
  }

  getStatusChipClass(status: string): string {
    const key = String(status ?? '').toLowerCase().trim();
    if (key === 'active') {
      return 'created-policies-details__status--active';
    }
    if (key === 'inactive' || key === 'expired' || key === 'deleted' || key === 'revoked') {
      return 'created-policies-details__status--inactive';
    }
    return 'created-policies-details__status--default';
  }

  formatAssetTypeLabel(rawType: string): string {
    if (!rawType) {
      return '-';
    }
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) {
      return 'Dataset';
    }
    if (t.includes('aimodel') || t.includes('model')) {
      return 'Model';
    }
    if (t.includes('app') || t.includes('usecase')) {
      return 'App';
    }
    const withoutNamespace = rawType.includes(':') ? rawType.split(':').pop() ?? rawType : rawType;
    return this.toTitleCase(withoutNamespace);
  }

  hasPersonDetails(person: PolicyPersonDetails): boolean {
    return [person.firstName, person.lastName, person.email, person.organization].some(
      (value) => !!String(value ?? '').trim() && value !== '-',
    );
  }

  getDisplayName(person: PolicyPersonDetails): string {
    const first = (person.firstName ?? '').trim();
    const last = (person.lastName ?? '').trim();
    if (first && last && first !== last && first !== '-' && last !== '-') {
      return `${first} ${last}`;
    }
    const single = first !== '-' ? first : last !== '-' ? last : '';
    return single || person.email || '-';
  }

  getInitials(person: PolicyPersonDetails): string {
    const name = this.getDisplayName(person);
    const parts = name.split(/[\s@.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }

  private buildPolicyDetails(item: Record<string, unknown>): PolicyDetailsView {
    const asset = this.readObject(item, 'asset');
    const constraints = this.readObject(item, 'constraints');
    const user = this.readObject(item, 'user');
    const owner = this.readObject(item, 'owner');
    const subjects = constraints ? this.readObject(constraints, 'subjects') : null;

    const rawAssetType = this.readFirstString(asset ?? {}, ['assetType', 'asset_type', 'type']);

    const rawPolicyType = this.readFirstString(item, ['policyType', 'policy_type']);
    const rawExpiryAt =
      this.readFirstString(item, ['expiryAt', 'expiry_at', 'expiryTime', 'expiry_time', 'expiresAt', 'expires_at']) ||
      this.readExpiryFromConstraints(constraints);

    return {
      status: this.resolvePolicyStatus(item, rawExpiryAt),
      policyType: rawPolicyType ? this.toTitleCase(rawPolicyType) : '-',
      createdAt: this.formatDateText(
        this.readFirstString(item, ['createdAt', 'created_at', 'itemCreatedAt', 'item_created_at']),
      ),
      updatedAt: this.formatDateText(this.readFirstString(item, ['updatedAt', 'updated_at'])),
      expiryAt: this.formatExpiryDateText(rawExpiryAt),
      owner: this.readPolicyPersonDetails(owner, 'owner'),
      user: this.readPolicyPersonDetails(user, 'consumer'),
      asset: {
        assetName: this.readFirstString(asset ?? {}, ['assetName', 'asset_name', 'itemName', 'name']) || '-',
        assetType: this.formatAssetTypeLabel(rawAssetType),
        shortDescription: this.readFirstString(asset ?? {}, ['shortDescription', 'short_description', 'description']) || '-',
        organization: this.readFirstString(asset ?? {}, ['organization', 'organisation']) || '-',
      },
      accessConstraints: this.readAccessConstraintDetails(constraints, rawExpiryAt),
      allowedRoles: this.readStringArray(subjects, ['allowedRoles', 'allowed_roles']),
      allowedOrgs: this.readStringArray(subjects, ['allowedOrgIds', 'allowed_org_ids']),
      providerComment: this.readFirstString(item, [
        'providerComment',
        'provider_comment',
        'internalRemark',
        'internal_remark',
      ]),
      feedbackToConsumer: this.readFirstString(item, [
        'feedbackToConsumer',
        'feedback_to_consumer',
        'remarkToConsumer',
        'remark_to_consumer',
        'feedbackComment',
        'feedback_comment',
      ]),
    };
  }

  showCommentsSection(details: PolicyDetailsView): boolean {
    return this.showInternalNotes(details) || this.showRequesterComment(details);
  }

  showInternalNotes(details: PolicyDetailsView): boolean {
    return (
      this.policyPortal !== 'consumer' &&
      this.policyListType !== 'owned' &&
      !this.hasPolicyTypeFilter() &&
      !!details.providerComment?.trim()
    );
  }

  showRequesterComment(details: PolicyDetailsView): boolean {
    return !!details.feedbackToConsumer?.trim();
  }

  formatPolicyTypeLabel(type: string): string {
    return this.toTitleCase(String(type ?? ''));
  }

  isGroupPolicyType(type: string): boolean {
    return String(type ?? '').trim().toLowerCase() === 'group';
  }

  getPolicyTypeTileClass(type: string): string {
    return this.isGroupPolicyType(type)
      ? 'pd__scope-tile-icon--group'
      : 'pd__scope-tile-icon--individual';
  }

  formatRoleLabel(role: string): string {
    return this.toTitleCase(String(role ?? '').replace(/-/g, ' '));
  }

  isSameText(a: string, b: string): boolean {
    return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
  }

  showSecondaryTimestamp(details: PolicyDetailsView): boolean {
    if (!details.updatedAt || details.updatedAt === '-') {
      return false;
    }
    if (this.selectedPolicyStatus === 'DELETED') {
      return true;
    }
    return !this.isSameText(details.createdAt, details.updatedAt);
  }

  getSecondaryTimestampLabel(): string {
    return this.selectedPolicyStatus === 'DELETED' ? 'Deleted' : 'Updated';
  }

  showScopeSection(details: PolicyDetailsView): boolean {
    return (
      details.allowedRoles.length > 0 ||
      !!details.user.organization.trim() ||
      (!!details.policyType && details.policyType !== '-')
    );
  }

  private readPolicyPersonDetails(
    person: Record<string, unknown> | null,
    role: 'consumer' | 'owner',
  ): PolicyPersonDetails {
    const firstNameKeys =
      role === 'consumer'
        ? ['consumerFirstName', 'consumer_first_name', 'firstName', 'first_name']
        : ['ownerFirstName', 'owner_first_name', 'firstName', 'first_name'];
    const lastNameKeys =
      role === 'consumer'
        ? ['consumerLastName', 'consumer_last_name', 'lastName', 'last_name']
        : ['ownerLastName', 'owner_last_name', 'lastName', 'last_name'];
    const emailKeys =
      role === 'consumer'
        ? ['consumerEmail', 'consumer_email', 'email', 'emailAddress']
        : ['ownerEmail', 'owner_email', 'email', 'emailAddress'];
    const organizationKeys =
      role === 'consumer'
        ? ['consumerOrganization', 'consumer_organization', 'organization', 'organisation']
        : ['ownerOrganization', 'owner_organization', 'organization', 'organisation'];

    return {
      firstName: this.readFirstString(person ?? {}, firstNameKeys) || '-',
      lastName: this.readFirstString(person ?? {}, lastNameKeys) || '-',
      email: this.readFirstString(person ?? {}, emailKeys) || '-',
      organization: this.readFirstString(person ?? {}, organizationKeys) || '-',
    };
  }

  private formatUserDisplayName(firstName: string, lastName: string, email: string): string {
    const first = firstName.trim();
    const last = lastName.trim();
    if (first && last && first === last) {
      return first;
    }
    const combined = `${first} ${last}`.trim();
    if (!combined) {
      return email || '-';
    }
    return combined;
  }

  private readCreatedBy(
    item: Record<string, unknown>,
    owner: Record<string, unknown> | null,
  ): { name: string; role: string } {
    const createdBy =
      this.readObject(item, 'createdBy') ?? this.readObject(item, 'created_by');
    const person = createdBy ?? owner;
    const ownerDetails = this.readPolicyPersonDetails(person, 'owner');
    const nameFromOwner = this.getDisplayName(ownerDetails);

    const username = this.readFirstString(person ?? {}, [
      'username',
      'userName',
      'user_name',
      'ownerUsername',
      'owner_username',
      'preferredUsername',
      'preferred_username',
    ]);
    const name =
      (nameFromOwner && nameFromOwner !== '-' ? nameFromOwner : '') ||
      username ||
      this.readFirstString(person ?? {}, ['name', 'displayName', 'display_name']) ||
      this.readFirstString(item, [
        'createdByName',
        'created_by_name',
        'createdByUsername',
        'created_by_username',
        'ownerFirstName',
      ]) ||
      '-';

    return {
      name,
      role: this.readApprovedByRole(item, owner, createdBy),
    };
  }

  private readApprovedBy(
    item: Record<string, unknown>,
    owner: Record<string, unknown> | null,
  ): { name: string; role: string } {
    const approvedBy =
      this.readObject(item, 'approvedBy') ?? this.readObject(item, 'approved_by');
    const person = approvedBy ?? owner;
    const ownerDetails = this.readPolicyPersonDetails(person, 'owner');
    const nameFromOwner = this.getDisplayName(ownerDetails);

    const username = this.readFirstString(person ?? {}, [
      'username',
      'userName',
      'user_name',
      'ownerUsername',
      'owner_username',
      'preferredUsername',
      'preferred_username',
    ]);
    const name =
      (nameFromOwner && nameFromOwner !== '-' ? nameFromOwner : '') ||
      username ||
      this.readFirstString(person ?? {}, ['name', 'displayName', 'display_name']) ||
      this.readFirstString(item, [
        'approvedByName',
        'approved_by_name',
        'approvedByUsername',
        'approved_by_username',
        'ownerFirstName',
      ]) ||
      '-';

    return {
      name,
      role: this.readApprovedByRole(item, owner, approvedBy),
    };
  }

  private readApprovedByRole(
    item: Record<string, unknown>,
    owner: Record<string, unknown> | null,
    approvedBy: Record<string, unknown> | null,
  ): string {
    const roleKeys = [
      'ownerRole',
      'owner_role',
      'approvedAs',
      'approved_as',
      'approvedByRole',
      'approved_by_role',
      'role',
      'userRole',
      'user_role',
    ];
    const fromApprovedBy = this.readFirstString(approvedBy ?? {}, roleKeys);
    if (fromApprovedBy) {
      return this.formatApprovedByRole(fromApprovedBy);
    }
    const fromOwner = this.readFirstString(owner ?? {}, roleKeys);
    if (fromOwner) {
      return this.formatApprovedByRole(fromOwner);
    }
    const fromItem = this.readFirstString(item, [
      'approvedByRole',
      'approved_by_role',
      'ownerRole',
      'owner_role',
      'approvedAs',
      'approved_as',
    ]);
    if (fromItem) {
      return this.formatApprovedByRole(fromItem);
    }
    const fromRoles =
      this.readRoleStrings(approvedBy, ['roles', 'ownerRoles', 'owner_roles']) ||
      this.readRoleStrings(owner, ['roles', 'ownerRoles', 'owner_roles']);
    if (fromRoles.length) {
      const delegateRole = fromRoles.find((role) => /delegate/i.test(role));
      return this.formatApprovedByRole(delegateRole || fromRoles[0]);
    }
    return '';
  }

  private readRoleStrings(
    source: Record<string, unknown> | null,
    keys: string[],
  ): string[] {
    if (!source) {
      return [];
    }
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return [value.trim()];
      }
      if (!Array.isArray(value)) {
        continue;
      }
      const roles = value
        .map((entry) => this.readRoleEntry(entry))
        .filter((role) => role && !this.isSystemRole(role));
      if (roles.length) {
        return roles;
      }
    }
    return [];
  }

  private readRoleEntry(entry: unknown): string {
    if (typeof entry === 'string') {
      return entry.trim();
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return '';
    }
    return this.readFirstString(entry as Record<string, unknown>, [
      'role',
      'roleName',
      'role_name',
      'name',
      'value',
    ]);
  }

  private isSystemRole(role: string): boolean {
    const normalized = role.toLowerCase();
    return (
      normalized.startsWith('default-roles-') ||
      normalized === 'offline_access' ||
      normalized === 'uma_authorization'
    );
  }

  private formatApprovedByRole(value: string): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private readAccessConstraintDetails(
    constraints: Record<string, unknown> | null,
    fallbackExpiry = '',
  ): PolicyAccessConstraintDetail[] {
    if (!constraints) {
      return [];
    }
    const access = constraints['access'];
    if (!Array.isArray(access)) {
      return [];
    }
    const details: PolicyAccessConstraintDetail[] = [];
    for (const entry of access) {
      // Some list responses return bare access-type strings.
      if (typeof entry === 'string' && entry.trim()) {
        details.push({
          accessType: entry.trim(),
          expiry: fallbackExpiry ? this.formatExpiryDateText(fallbackExpiry) : '-',
        });
        continue;
      }
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        continue;
      }
      const row = entry as Record<string, unknown>;
      const accessType = this.readFirstString(row, ['accessType', 'type']);
      if (!accessType) {
        continue;
      }
      const expiryRaw =
        this.readFirstString(row, this.expiryFieldKeys) || fallbackExpiry;
      details.push({
        accessType,
        expiry: expiryRaw ? this.formatExpiryDateText(expiryRaw) : '-',
      });
    }
    return details;
  }

  private readStringArray(
    item: Record<string, unknown> | null,
    keys: string | string[],
  ): string[] {
    if (!item) {
      return [];
    }
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
      const value = item[key];
      if (!Array.isArray(value)) {
        continue;
      }
      const parsed = value.map((entry) => String(entry ?? '').trim()).filter(Boolean);
      if (parsed.length) {
        return parsed;
      }
    }
    return [];
  }

  private extractList(response: unknown): Record<string, unknown>[] {
    if (!response || typeof response !== 'object') {
      return [];
    }
    const r = response as Record<string, unknown>;
    if (Array.isArray(r['result'])) {
      return r['result'] as Record<string, unknown>[];
    }
    if (Array.isArray(r['data'])) {
      return r['data'] as Record<string, unknown>[];
    }
    const nestedResult = r['result'];
    if (nestedResult && typeof nestedResult === 'object') {
      const nested = nestedResult as Record<string, unknown>;
      if (Array.isArray(nested['results'])) {
        return nested['results'] as Record<string, unknown>[];
      }
    }
    return [];
  }

  private extractTotal(response: unknown, fallback: number): number {
    if (!response || typeof response !== 'object') {
      return fallback;
    }
    const r = response as Record<string, unknown>;
    const candidates = [
      r['totalCount'],
      r['total'],
      (r['paginationInfo'] as Record<string, unknown> | undefined)?.['totalCount'],
      (r['pagination'] as Record<string, unknown> | undefined)?.['totalCount'],
    ];
    for (const candidate of candidates) {
      const num = Number(candidate);
      if (Number.isFinite(num) && num >= 0) {
        return num;
      }
    }
    return fallback;
  }
}

